import { type FC, useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $hasDeleteMediaPermission } from '@/models/permissions'
import {
  $selectedCategory,
  deleteVaultCategoryFx,
} from '@/models/vault/categories.ts'

interface Props extends DialogProps {}

export const VaultDeleteCategoryDialog: FC<Props> = ({ isOpen, onClose }) => {
  const selectedCategory = useUnit($selectedCategory)
  const isLoading = useUnit(deleteVaultCategoryFx.pending)
  const hasDeleteMediaPermission = useUnit($hasDeleteMediaPermission)

  const [clearMedia, setClearMedia] = useState(false)

  const handleSave = useCallback(async () => {
    await deleteVaultCategoryFx({
      categoryId: selectedCategory!.id,
      clearMedia,
    })
    onClose()
  }, [clearMedia, onClose, selectedCategory])

  const handleClearMediaToggle = useCallback(() => {
    setClearMedia((old) => !old)
  }, [])

  return (
    <Dialog show={isOpen}>
      <Dialog.Header>
        <Dialog.Title>Delete Category</Dialog.Title>
      </Dialog.Header>

      <div className="mt-5">
        <p className="mb-3">
          Are you sure you want to delete the category{' '}
          <b>{selectedCategory?.name}</b>?
        </p>
        {hasDeleteMediaPermission && (
          <div>
            <Checkbox
              checked={clearMedia}
              onCheckedChange={handleClearMediaToggle}
            />
            <span className="ml-2 text-sm italic">
              Delete media inside this category from the Vault irrevocably
            </span>
          </div>
        )}
      </div>

      <Dialog.Footer className="grid grid-cols-2 gap-2">
        <Button
          disabled={isLoading}
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="destructive"
          isLoading={isLoading}
        >
          Delete
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
